/**
 * Утилиты для работы со structured основным исходом (MainOutcome)
 * и политикой ничьей (DrawPolicy) на frontend.
 *
 * Используются в формах ставки, фиксации результата и отображении прогнозов/результатов.
 */

// ===================================================================================================================
// = OutcomeCode & DrawPolicy constants
// ===================================================================================================================

export const OutcomeCode = Object.freeze({
  P1: 'P1',
  X: 'X',
  P2: 'P2',
  X1: 'X1',
  X2: 'X2',
});

export const DrawPolicy = Object.freeze({
  DRAW_ALLOWED: 'DRAW_ALLOWED',
  DRAW_FORBIDDEN: 'DRAW_FORBIDDEN',
  DRAW_WITH_TIE_BREAK_WINNER: 'DRAW_WITH_TIE_BREAK_WINNER',
});

const OUTCOME_LABELS = Object.freeze({
  [OutcomeCode.P1]: 'Победа 1-й стороны (P1)',
  [OutcomeCode.X]: 'Ничья (X)',
  [OutcomeCode.P2]: 'Победа 2-й стороны (P2)',
  [OutcomeCode.X1]: 'Ничья, преим. 1-й (X1)',
  [OutcomeCode.X2]: 'Ничья, преим. 2-й (X2)',
});

const OUTCOME_SHORT_LABELS = Object.freeze({
  [OutcomeCode.P1]: 'P1',
  [OutcomeCode.X]: 'X',
  [OutcomeCode.P2]: 'P2',
  [OutcomeCode.X1]: 'X1',
  [OutcomeCode.X2]: 'X2',
});

// ===================================================================================================================
// = Допустимые исходы по DrawPolicy
// ===================================================================================================================

/**
 * Возвращает список допустимых OutcomeCode для заданной политики ничьей.
 */
export function allowedOutcomeCodes(drawPolicy) {
  switch (drawPolicy) {
    case DrawPolicy.DRAW_FORBIDDEN:
      return [OutcomeCode.P1, OutcomeCode.P2];
    case DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER:
      return [OutcomeCode.P1, OutcomeCode.P2, OutcomeCode.X1, OutcomeCode.X2];
    case DrawPolicy.DRAW_ALLOWED:
    default:
      return [OutcomeCode.P1, OutcomeCode.X, OutcomeCode.P2];
  }
}

/**
 * Возвращает список допустимых OutcomeCode при текущем счёте и политике.
 * Если счёт равный и DRAW_FORBIDDEN — возвращает пустой массив (ошибка).
 */
export function outcomeCodesForScore(homeScore, awayScore, drawPolicy) {
  const comparison = Number(homeScore) - Number(awayScore);
  if (comparison > 0) return [OutcomeCode.P1];
  if (comparison < 0) return [OutcomeCode.P2];

  // Ничейный счёт
  switch (drawPolicy) {
    case DrawPolicy.DRAW_FORBIDDEN:
      return [];
    case DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER:
      return [OutcomeCode.X1, OutcomeCode.X2];
    case DrawPolicy.DRAW_ALLOWED:
    default:
      return [OutcomeCode.X];
  }
}

// ===================================================================================================================
// = Валидация
// ===================================================================================================================

const SCORE_INVALID_MESSAGE = 'Счёт должен быть целым неотрицательным числом';
const DRAW_FORBIDDEN_MESSAGE = 'Ничейный счёт запрещён при текущей политике';
const OUTCOME_MISMATCH_MESSAGE = 'Код исхода не соответствует счёту';
const OUTCOME_REQUIRED_MESSAGE = 'Выбери код исхода';

/**
 * Валидирует structured прогноз/результат.
 * Возвращает объект { valid, errors } где errors — { homeScore, awayScore, outcomeCode }.
 */
export function validateMainOutcome(homeScore, awayScore, outcomeCode, drawPolicy) {
  const errors = {};

  const home = parseScore(homeScore);
  const away = parseScore(awayScore);

  if (home === null) errors.homeScore = SCORE_INVALID_MESSAGE;
  if (away === null) errors.awayScore = SCORE_INVALID_MESSAGE;

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }

  if (!outcomeCode) {
    errors.outcomeCode = OUTCOME_REQUIRED_MESSAGE;
    return { valid: false, errors };
  }

  const allowed = outcomeCodesForScore(home, away, drawPolicy);

  if (allowed.length === 0) {
    errors.homeScore = DRAW_FORBIDDEN_MESSAGE;
    errors.awayScore = DRAW_FORBIDDEN_MESSAGE;
    return { valid: false, errors };
  }

  if (!allowed.includes(outcomeCode)) {
    errors.outcomeCode = OUTCOME_MISMATCH_MESSAGE;
    return { valid: false, errors };
  }

  return { valid: true, errors: {} };
}

function parseScore(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  if (!Number.isInteger(number) || number < 0) return null;
  return number;
}

// ===================================================================================================================
// = Форматирование
// ===================================================================================================================

/**
 * Форматирует MainOutcome в человекочитаемую строку вида «3-1 P1».
 */
export function formatMainOutcome(mainOutcome) {
  if (!mainOutcome || mainOutcome.homeScore == null || mainOutcome.awayScore == null) {
    return null;
  }
  const score = `${mainOutcome.homeScore}-${mainOutcome.awayScore}`;
  if (mainOutcome.outcomeCode) {
    return `${score} ${mainOutcome.outcomeCode}`;
  }
  return score;
}

/**
 * Формирует строки детализации результата события: основное время, доп. время, пенальти, итог.
 */
export function formatEventResultBreakdown(resultBreakdown) {
  if (!resultBreakdown) {
    return [];
  }
  return [
    resultLine('Основное время', resultBreakdown.regularHomeScore, resultBreakdown.regularAwayScore),
    resultLine('Доп. время', resultBreakdown.extraHomeScore, resultBreakdown.extraAwayScore),
    resultLine('Пенальти', resultBreakdown.penaltyHomeScore, resultBreakdown.penaltyAwayScore),
    resultLine('Итог', resultBreakdown.finalHomeScore, resultBreakdown.finalAwayScore),
  ].filter(Boolean);
}

function resultLine(label, homeScore, awayScore) {
  if (homeScore == null || awayScore == null) {
    return null;
  }
  return { label, value: `${homeScore}-${awayScore}` };
}

/**
 * Возвращает человекочитаемый лейбл для OutcomeCode.
 */
export function outcomeCodeLabel(code) {
  return OUTCOME_LABELS[code] || code;
}

/**
 * Возвращает короткий лейбл для OutcomeCode (P1, X, P2, X1, X2).
 */
export function outcomeCodeShortLabel(code) {
  return OUTCOME_SHORT_LABELS[code] || code;
}

/**
 * Определяет, является ли пари score-based (имеет drawPolicy → structured ставки).
 */
export function isScoreBasedChallenge(challenge) {
  return Boolean(challenge?.drawPolicy);
}
